// Labeled input with a colored left stripe + confidence pill in the top-right.
// Used throughout the review form so the tech sees the AI's certainty per field.

import { type ReactNode } from "react";

export type Confidence = number | null;

function confClass(c: Confidence): { stripe: string; pill: string } {
  if (c === null) return { stripe: "bg-border",    pill: "bg-pill text-ink-soft" };
  if (c >= 0.85)   return { stripe: "bg-good",      pill: "bg-good-soft text-good" };
  if (c >= 0.6)    return { stripe: "bg-warn",      pill: "bg-warn-soft text-warn" };
  return                 { stripe: "bg-bad",       pill: "bg-bad-soft text-bad" };
}

type Props = {
  label: string;
  value?: string | number | null;
  confidence?: Confidence;
  onChange?: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
  disabled?: boolean;
  type?: "text" | "date" | "number";
  children?: ReactNode; // for select / custom controls — replaces the input
};

export function ConfidenceField({
  label, value, confidence = null, onChange, placeholder, multiline, disabled, type = "text", children,
}: Props) {
  const cls = confClass(confidence);
  const inputClasses = "w-full pr-14 pl-2.5 py-1.5 border border-border rounded text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent disabled:bg-pill";
  const stringValue = value === null || value === undefined ? "" : String(value);

  return (
    <div className="grid grid-cols-[110px_1fr] gap-2 items-start mb-2">
      <label className="text-ink-soft text-xs pt-1.5">{label}</label>
      <div className="relative">
        <span className={`absolute -left-1 top-1 bottom-1 w-[3px] rounded-sm ${cls.stripe}`} aria-hidden />
        {children ? (
          <div className="pr-14">{children}</div>
        ) : multiline ? (
          <textarea
            value={stringValue}
            placeholder={placeholder}
            disabled={disabled}
            onChange={(e) => onChange?.(e.target.value)}
            className={`${inputClasses} min-h-[56px] resize-y`}
          />
        ) : (
          <input
            type={type}
            value={stringValue}
            placeholder={placeholder}
            disabled={disabled}
            onChange={(e) => onChange?.(e.target.value)}
            className={inputClasses}
          />
        )}
        {confidence !== null && (
          <span className={`absolute right-1.5 top-1.5 text-[11px] leading-none px-1.5 py-1 rounded ${cls.pill}`}>
            {confidence.toFixed(2)}
          </span>
        )}
      </div>
    </div>
  );
}
